/**
 * Game Time Controller
 * 
 * Provides centralized control over game time, supporting:
 * - Pause/Resume functionality
 * - Time scaling (slow-mo, speed-up)
 * - Smooth time ramping with configurable easing
 * - Multiple time layers (game time, UI time, etc.)
 * 
 * Usage:
 *   gameTime.pause();
 *   gameTime.resume();
 *   gameTime.setTimeScale(0.5); // Slow motion
 *   gameTime.rampTo(0.2, 1.0, 'easeOut'); // Ramp to 20% speed over 1 second
 */

import { EventBus } from './EventBus';
import { EasingFunction, EasingPreset, resolveEasing } from './Easing';

/**
 * Configuration for time ramping
 */
export interface TimeRampConfig {
  /** Target time scale (0 = paused, 1 = normal, 2 = double speed) */
  targetScale: number;
  /** Duration of the ramp in real seconds */
  duration: number;
  /** Easing function or preset for the ramp */
  easing?: EasingFunction | EasingPreset;
  /** Callback when ramp completes */
  onComplete?: () => void;
}

/**
 * Current state of a time ramp transition
 */
interface ActiveRamp {
  startScale: number;
  targetScale: number;
  duration: number;
  elapsed: number;
  easing: EasingFunction;
  onComplete?: () => void;
}

/**
 * Game time state snapshot for debugging
 */
export interface GameTimeState {
  timeScale: number;
  isPaused: boolean;
  isRamping: boolean;
  targetScale: number | null;
  rampProgress: number | null;
}

/**
 * Centralized game time controller
 */
export class GameTime {
  private _timeScale: number = 1.0;
  private _isPaused: boolean = false;
  private _pausedTimeScale: number = 1.0;
  private _activeRamp: ActiveRamp | null = null;
  private _eventBus: EventBus | null = null;

  /**
   * Current time scale (0 = frozen, 1 = normal, <1 = slow, >1 = fast)
   */
  get timeScale(): number {
    return this._isPaused ? 0 : this._timeScale;
  }

  /**
   * Whether the game is currently paused
   */
  get isPaused(): boolean {
    return this._isPaused;
  }

  /**
   * Whether a time ramp is currently in progress
   */
  get isRamping(): boolean {
    return this._activeRamp !== null;
  }

  /**
   * Raw time scale (ignoring pause state)
   */
  get rawTimeScale(): number {
    return this._timeScale;
  }

  /**
   * Connect to EventBus for broadcasting time changes
   */
  setEventBus(eventBus: EventBus): void {
    this._eventBus = eventBus;
  }

  /**
   * Pause the game (preserves current time scale for resume)
   */
  pause(): void {
    if (this._isPaused) return;
    
    this._isPaused = true;
    this._pausedTimeScale = this._timeScale;
    this.emitChange();
  }

  /**
   * Resume from pause (restores previous time scale)
   */
  resume(): void {
    if (!this._isPaused) return;
    
    this._isPaused = false;
    this._timeScale = this._pausedTimeScale;
    this.emitChange();
  }

  /**
   * Toggle pause state
   */
  togglePause(): void {
    if (this._isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  /**
   * Set time scale immediately (cancels any active ramp)
   * @param scale Time scale (0-∞, typically 0-2)
   */
  setTimeScale(scale: number): void {
    this.cancelRamp();
    this._timeScale = Math.max(0, scale);
    
    if (this._isPaused && scale > 0) {
      this._pausedTimeScale = scale;
    }
    
    this.emitChange();
  }

  /**
   * Smoothly ramp to a target time scale
   * @param targetScale Target time scale
   * @param duration Duration in real seconds
   * @param easing Easing function or preset (default: 'easeInOut')
   * @param onComplete Optional callback when ramp finishes
   */
  rampTo(
    targetScale: number,
    duration: number,
    easing?: EasingFunction | EasingPreset,
    onComplete?: () => void
  ): void {
    if (duration <= 0) {
      this.setTimeScale(targetScale);
      onComplete?.();
      return;
    }

    this._activeRamp = {
      startScale: this._timeScale,
      targetScale: Math.max(0, targetScale),
      duration,
      elapsed: 0,
      easing: resolveEasing(easing, 'easeInOut'),
      onComplete,
    };
  }

  /**
   * Ramp to slow motion
   * @param scale Target slow-mo scale (default: 0.2)
   * @param duration Ramp duration in seconds (default: 0.3)
   */
  slowMotion(scale: number = 0.2, duration: number = 0.3): void {
    this.rampTo(scale, duration, 'easeOut');
  }

  /**
   * Ramp back to normal speed
   * @param duration Ramp duration in seconds (default: 0.3)
   */
  normalSpeed(duration: number = 0.3): void {
    this.rampTo(1.0, duration, 'easeIn');
  }

  /**
   * Ramp to fast forward
   * @param scale Target speed (default: 2.0)
   * @param duration Ramp duration in seconds (default: 0.2)
   */
  fastForward(scale: number = 2.0, duration: number = 0.2): void {
    this.rampTo(scale, duration, 'easeOut');
  }

  /**
   * Cancel any active time ramp
   */
  cancelRamp(): void {
    if (this._activeRamp?.onComplete) {
      // Don't call onComplete when cancelled
    }
    this._activeRamp = null;
  }

  /**
   * Update the time controller (call every frame with real delta time)
   * @param realDeltaTime Delta time in seconds (NOT scaled)
   */
  update(realDeltaTime: number): void {
    if (!this._activeRamp) return;

    const ramp = this._activeRamp;
    ramp.elapsed += realDeltaTime;

    const t = Math.min(ramp.elapsed / ramp.duration, 1.0);
    const easedT = ramp.easing(t);
    
    this._timeScale = ramp.startScale + (ramp.targetScale - ramp.startScale) * easedT;

    if (t >= 1.0) {
      const onComplete = ramp.onComplete;
      this._activeRamp = null;
      this.emitChange();
      onComplete?.();
    }
  }

  /**
   * Get scaled delta time from raw delta time
   * @param realDeltaTime Real delta time in seconds
   * @returns Scaled delta time
   */
  scale(realDeltaTime: number): number {
    return realDeltaTime * this.timeScale;
  }

  /**
   * Get current state for debugging
   */
  getState(): GameTimeState {
    return {
      timeScale: this.timeScale,
      isPaused: this._isPaused,
      isRamping: this._activeRamp !== null,
      targetScale: this._activeRamp?.targetScale ?? null,
      rampProgress: this._activeRamp 
        ? Math.min(this._activeRamp.elapsed / this._activeRamp.duration, 1.0) 
        : null,
    };
  }

  /**
   * Reset to default state
   */
  reset(): void {
    this.cancelRamp();
    this._timeScale = 1.0;
    this._isPaused = false;
    this._pausedTimeScale = 1.0;
    this.emitChange();
  }

  private emitChange(): void {
    this._eventBus?.emit('gameTime:changed', this.getState());
  }
}

/**
 * Global game time instance
 */
export const gameTime = new GameTime();
